import type { Edge } from '@xyflow/react'
import type { ListCopy } from 'lib/listCopy'
import { isOnTaskPath, isOrphanOutcomeNode, unreachableNodeIds } from './graphUtils'
import { nodesWithGuaranteedMembership } from './pipelineMembership'
import { serializeWorkflow } from './serialize'
import type { WorkflowNode, WorkflowSettings } from 'app/types/workflows'
import { type NodeErrors, type ValidationContext, nodeDataErrors, nodeDataWarnings } from './validation'

/** Blocking errors + non-blocking warnings for one node. The single source of truth consumed by the
 *  node-card badge, the inspector banner/field errors, and the toolbar "N to fix" count — so all
 *  three always agree (previously each computed its own, from different context subsets). */
export type NodeIssues = { errors: NodeErrors; warnings: NodeErrors }

const EMPTY: NodeIssues = { errors: {}, warnings: {} }

/** Compute issues for every node once: per-node data validation (pure) PLUS graph-aware checks
 *  (disconnected from the trigger; outcome node with no upstream action). */
export function computeNodeIssues(
  nodes: WorkflowNode[],
  edges: Edge[],
  ctx: ValidationContext,
  copy: ListCopy,
  settings?: WorkflowSettings,
  /** The chosen location. Passed through to the serializer so the issues panel agrees with the save
   *  path: without it the panel kept demanding a location the user had already chosen. */
  locationId?: string,
  /** Whether that location can actually hold a wait for business hours (validation's
   *  `locationHasBusinessHours`). Same reason as above: the panel and the save path must agree. */
  locationHasBusinessHours?: boolean,
  /** Whether the texting category still waits on an admin's save. Same reason: the trigger's
   *  warning must clear once the engine holds the category. */
  categoryPending?: boolean,
): Map<string, NodeIssues> {
  const unreachable = unreachableNodeIds(nodes, edges)
  const map = new Map<string, NodeIssues>()
  for (const node of nodes) {
    const errors: NodeErrors = { ...nodeDataErrors(node.type, node.data, copy, ctx) }
    const warnings: NodeErrors = { ...nodeDataWarnings(node.type, node.data, ctx) }

    if (unreachable.has(node.id)) {
      errors.unreachable = `This step isn’t connected to the workflow, so it never runs.`
    } else if (isOrphanOutcomeNode(node.id, node.type, nodes, edges)) {
      errors.upstream =
        node.type === `waitForCallResult`
          ? `Place this after an Initiate call: there’s no call to react to.`
          : `Place this after a Send SMS: there’s no conversation to wait on.`
    } else if (node.type === `editTask` && !isOnTaskPath(node.id, nodes, edges)) {
      errors.upstream = `Place this on a Create task's task path: there's no task to edit here.`
    }

    map.set(node.id, { errors, warnings })
  }

  // The F2 self-trigger loop warning is retired: the engine's loop guard is live and verified
  // (TRK-5362, 2026-07-29 — a workflow's own contact edits caused zero re-enrollments across
  // multiple evaluation cycles, plus a 3/hour per-contact rate cap as backstop).

  // A Move step is an upsert at the engine (TRK-5674), so a contact who isn't in the pipeline is
  // enrolled rather than skipped. The step's own copy says so, but only the graph can say whether
  // that can actually happen here, so flag the Move steps nothing upstream rules out. Advisory: the
  // author may know something the canvas doesn't, and this must not gate Activate.
  const guaranteedFor = new Map<string, Set<string>>()
  for (const node of nodes) {
    if (node.type !== `addToPipeline` || node.data.operation !== `move`) continue
    const pipelineId = node.data.pipelineId
    const entry = map.get(node.id)
    // No pipeline chosen is already a blocking error, and an island never runs. Neither needs this.
    if (!pipelineId || !entry || entry.errors.unreachable) continue
    let guaranteed = guaranteedFor.get(pipelineId)
    if (!guaranteed) {
      guaranteed = nodesWithGuaranteedMembership(pipelineId, nodes, edges)
      guaranteedFor.set(pipelineId, guaranteed)
    }
    if (guaranteed.has(node.id)) continue
    const where = node.data.pipelineName || copy.thisList
    entry.warnings.pipelineMembership =
      `Nothing above this step checks that the contact is in ${where}, and a contact who isn’t in it ` +
      `gets added${node.data.stage ? ` at ${node.data.stage}` : ``} rather than skipped. Add a branch on ` +
      copy.stageAboveThisStep
  }

  // Fold in engine-capability gaps (keep the node authorable, flag what the engine can't run yet):
  // an unsupported node is a BLOCKING error (gates Activate); a lossy field / trigger mapping / send-
  // time setting is a non-blocking warning. Workflow-level gaps attach to the trigger node.
  // Workflow-level gaps anchor to the trigger — or to the first node when the graph is headless,
  // so deleting the trigger doesn't silently swallow errors like the missing location (08-24 audit).
  const triggerId = nodes.find((node) => node.type === `trigger`)?.id ?? nodes[0]?.id
  const compiled = serializeWorkflow({
    locationId,
    locationHasBusinessHours,
    name: ``,
    triggerStartDate: ``,
    nodes,
    edges,
    settings,
    copy,
    categoryPending,
  })
  for (const gap of compiled.gaps) {
    const entry = map.get(gap.nodeId ?? triggerId ?? ``)
    if (!entry) continue
    if (gap.code === `unsupported_node` || gap.code === `unsupported_trigger` || gap.code === `unsupported_settings`) {
      entry.errors.needsBackend = entry.errors.needsBackend
        ? `${entry.errors.needsBackend} ${gap.message}`
        : gap.message
    } else {
      entry.warnings.engineGap = entry.warnings.engineGap ? `${entry.warnings.engineGap} ${gap.message}` : gap.message
    }
  }

  return map
}

export function nodeIssuesFor(map: Map<string, NodeIssues>, nodeId: string | null): NodeIssues {
  return (nodeId && map.get(nodeId)) || EMPTY
}

/** Total blocking issues across the workflow (gates Activate): every node's errors + "needs a trigger". */
export function totalIssueCount(map: Map<string, NodeIssues>, nodes: WorkflowNode[]): number {
  let count = 0
  for (const { errors } of map.values()) count += Object.keys(errors).length
  const needsTrigger = nodes.some((node) => node.type === `trigger`) ? 0 : 1
  return count + needsTrigger
}
